import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { setSentryCompany } from "@/lib/sentry";

export const useCompanySwitcher = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: companies, isLoading: loadingCompanies } = useQuery({
    queryKey: ["user-companies", user?.id],
    queryFn: async () => {
      if (!user) return [];

      const { data: roles, error } = await supabase
        .from("user_roles")
        .select("company_id, role, companies:company_id(id, name)")
        .eq("user_id", user.id)
        .eq("active", true);

      if (error) throw error;

      const seen = new Set<string>();
      const companyList = (roles || [])
        .filter((r) => {
          if (!r.company_id || seen.has(r.company_id)) return false;
          seen.add(r.company_id);
          return true;
        })
        .map((r) => ({
          id: r.company_id!,
          name: (r.companies as any)?.name || "Okänt företag",
        }));

      // Fetch subscription status for each company
      if (companyList.length > 0) {
        const { data: subs } = await supabase
          .from("subscriptions")
          .select("company_id, status, plan_id, subscription_plans:plan_id(display_name)")
          .in("company_id", companyList.map((c) => c.id));

        const subMap = new Map(
          (subs || []).map((s) => [
            s.company_id,
            {
              status: s.status,
              planName: (s.subscription_plans as any)?.display_name || null,
            },
          ])
        );

        return companyList.map((c) => ({
          ...c,
          subscriptionStatus: subMap.get(c.id)?.status || null,
          planName: subMap.get(c.id)?.planName || null,
        }));
      }

      return companyList.map((c) => ({ ...c, subscriptionStatus: null as string | null, planName: null as string | null }));
    },
    enabled: !!user,
  });

  const { data: activeCompanyId } = useQuery({
    queryKey: ["active-company", user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("id", user.id)
        .maybeSingle();
      if (error) throw error;
      return data?.company_id || null;
    },
    enabled: !!user,
  });

  const switchCompany = useMutation({
    mutationFn: async (companyId: string) => {
      if (!user) throw new Error("Inte inloggad");
      const { error } = await supabase
        .from("profiles")
        .update({ company_id: companyId })
        .eq("id", user.id);
      if (error) throw error;
    },
    onSuccess: async () => {
      // Switching company changes the active tenant for *every* query, so
      // invalidate the whole cache and let observed queries refetch under the
      // new context. invalidate (not clear) only marks data stale and refetches
      // mounted queries — it never drops cached data. A blanket invalidate avoids
      // stale-data bugs from queries whose keys don't include the company
      // (e.g. the dashboard count cards: ["tire-sets-count"], ["customers-count"]…),
      // at the cost of a few extra refetches on an infrequent action.
      await queryClient.invalidateQueries();
      toast.success("Företag bytt!");
    },
    onError: (error: any) => {
      toast.error(error.message || "Kunde inte byta företag");
    },
  });

  const activeCompany = companies?.find((c) => c.id === activeCompanyId) || null;

  // Tag every Sentry event with the active company so we can filter errors
  // per customer in the dashboard.
  useEffect(() => {
    setSentryCompany(activeCompanyId ?? null);
  }, [activeCompanyId]);

  return {
    companies: companies || [],
    activeCompany,
    activeCompanyId,
    loadingCompanies,
    switchCompany: switchCompany.mutate,
    // Awaitable variant for callers that need to know when the switch
    // (and the cascading cache invalidations) have completed — e.g.
    // CheckoutSuccess after creating an additional company.
    switchCompanyAsync: switchCompany.mutateAsync,
    isSwitching: switchCompany.isPending,
  };
};
